/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Button,
  Card,
  IconAdd,
  IconSearch,
  Table,
  TableTypes,
} from "tvs-unified-lib/dist";
import {
  Header,
  Root,
  Status,
  TextUploadFileName,
  TextUploadId,
  Title,
} from "./styles";
import { useNavigate } from "react-router-dom";
import { DatePickerField, InputField, SelectField } from "@/components";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDebounce } from "@/hooks";
import { uploadApi } from "@/services";
import { ColumnDef } from "@tanstack/react-table";
import moment from "moment";
import { DEFAULT_FORMAT_DATE } from "@/constants/common";
import { useSnackbar } from "notistack";

interface FormValues {
  created_date: string;
  status: any;
  search: string;
}

const optionsStatus = [
  {
    label: "All Statuses",
    value: "",
  },
  {
    label: "In Progress",
    value: "processing",
  },
  {
    label: "Successful",
    value: "success",
  },
  {
    label: "Failed",
    value: "failure",
  },
];

export const Upload = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [pageIndex, setPageIndex] = useState<any>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any>();

  const { control, watch } = useForm<FormValues>({
    defaultValues: {
      created_date: "",
      status: [],
      search: "",
    },
  });

  const fetchData = useDebounce(async () => {
    setLoading(true);
    const params = {
      page: pageIndex + 1,
      limit: 10,
      ...(watch("created_date") && {
        created_date: moment(watch("created_date")).format("YYYY-MM-DD"),
      }),
      ...(watch("status")[0]?.value && {
        status: watch("status")[0]?.value,
      }),
      ...(watch("search") && {
        search: watch("search"),
      }),
    };
    await uploadApi
      .list(params)
      .then(({ data }) => {
        if (!data) return;
        setData(data);
      })
      .catch((e) => {
        enqueueSnackbar(e, {
          variant: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, 500);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  useEffect(() => {
    setPageIndex(0);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("search"), watch("created_date"), watch("status")]);

  const handleRedirectCreateUpload = () => {
    navigate("/upload/new");
  };

  const handledownload = useDebounce(async (id, fileName) => {
    await uploadApi
      .downfile(id)
      .then(({ data }) => {
        if (data) {
          const link = document.createElement("a");
          link.href = data;

          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          enqueueSnackbar("File downloaded successfully.", {
            variant: "success",
          });
        }
      })
      .catch((e) => {
        enqueueSnackbar(e, {
          variant: "error",
        });
      });
  }, 500);

  const renderStatusColor: Record<string, any> = {
    success: {
      color: "#42AD47",
      bg: "#E7F5E8",
      text: "Successful",
    },
    processing: {
      color: "#395278",
      bg: "#E7E9ED",
      text: "In Progress",
    },
    failure: {
      color: "#EB4553",
      bg: "#FBEBF0",
      text: "Failed",
    },
  };

  const columns: ColumnDef<any>[] = [
    {
      header: () => "Upload ID",
      enableSorting: false,
      accessorKey: "uploadId",
      size: 200,
      cell: ({ getValue, row }: TableTypes.CellType<any>) => {
        const value = getValue();
        return (
          <TextUploadId to={`/upload/${row?.original?.id}`}>
            {value}
          </TextUploadId>
        );
      },
    },
    {
      header: () => "Status",
      enableSorting: false,
      accessorKey: "status",
      size: 200,
      cell: ({ getValue }: TableTypes.CellType<any>) => {
        const value = getValue();
        return (
          <Status
            bg={renderStatusColor[value].bg}
            color={renderStatusColor[value].color}
          >
            {renderStatusColor[value].text}
          </Status>
        );
      },
    },
    {
      header: () => "File Name",
      enableSorting: false,
      accessorKey: "fileName",
      size: 200,
      cell: ({ getValue, row }: TableTypes.CellType<any>) => {
        const value = getValue();
        return (
          <TextUploadFileName onClick={() => handledownload(row?.original?.id, value)}>
            {value}
          </TextUploadFileName>
        );
      },
    },
    {
      header: () => "Total Accounts",
      enableSorting: false,
      accessorKey: "successCount",
      cell: ({ getValue }: TableTypes.CellType<any>) => {
        const value = getValue() ? getValue() : "-";
        return value;
      },
    },
    {
      header: () => "Total Parameters",
      enableSorting: false,
      accessorKey: "excelHeaders",
      cell: ({ getValue }: TableTypes.CellType<any>) => {
        const value = getValue().length ? getValue().length : "-";
        return value;
      },
    },
    {
      header: () => "Created By",
      enableSorting: false,
      accessorKey: "createdBy",
      cell: ({ row }: TableTypes.CellType<any>) => {
        const { employeeId, firstName, lastName } = row.original.user;
        return (
          <div>
            {employeeId} <br /> {firstName} {lastName}
          </div>
        );
      },
    },
    {
      header: () => "Created Date",
      enableSorting: false,
      accessorKey: "createdAt",
      cell: ({ getValue }: TableTypes.CellType<any>) => {
        const value = getValue();
        return value ? moment(value).format(DEFAULT_FORMAT_DATE) : "-";
      },
    },
  ];

  const handlePageChange = (pageIndex: number) => {
    setPageIndex(pageIndex);
  };
  return (
    <Root>
      <Header>
        <Title>Upload</Title>
        <Button
          label="New Upload"
          icon={<IconAdd fill="white" />}
          iconPosition="left"
          theme="purple"
          onClick={handleRedirectCreateUpload}
        />
      </Header>
      <Card variant="no-border" className="card-filter">
        <DatePickerField
          name="created_date"
          control={control}
          placeholder="September 2024"
          showMonthYearPicker
          format="MMMM yyyy"
        />
        <SelectField
          name="status"
          control={control}
          placeholder="All Statuses"
          options={optionsStatus}
        />
        <InputField
          name="search"
          control={control}
          icon={<IconSearch />}
          placeholder="Search By Upload ID, File Name, Created By"
        />
      </Card>
      <Table
        columns={columns}
        data={data?.response || []}
        totalRecords={data?.totalCount || 0}
        isLoading={loading}
        manualSorting={false}
        pagination={{
          pageIndex: pageIndex,
          pageSize: 10,
        }}
        enablePagination={true}
        onPageChange={handlePageChange}
        sorting={[]}
        setSorting={() => {}}
      />
    </Root>
  );
};

export default Upload;
